import { dismissAlert, showError, showHttpError, showSuccess } from '@calendly/alert'
import { FormError } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { transform } from 'lodash-es'

import { moveToChangeSubscription } from 'actions/billing/subscription_modal'
import { setFormErrors } from 'actions/form'
import changeSet from 'core/change_set'
import { fetchStatistics } from 'features/organization_statistics'
import { createGroup, listGroups } from 'features/user_groups'
import { rootApi } from 'root-api'
import { isSingleSelector } from 'selectors/current_organization'
import { isAdminOrOwnerOrGroupAdminSelector, isOnlyGroupAdminSelector } from 'selectors/current_user'

import { createInvitations, getManagedEventTypes, validateInvitations } from './api'
import {
  managedEventTypesPaginationSelector,
  managedEventTypesSortOrderSelector,
  recaptchaCallbackSelector,
  selectedGroupIdSelector
} from './selectors'
import { STEPS } from './steps'
import { translateBackendErrorsToForm } from './utils'

const nextStep = createAction('inviteFlow/nextStep')
const moveToStep = createAction('inviteFlow/moveToStep')
const resetFormInvitations = createAction('inviteFlow/resetFormInvitations')
const saveBackendErrors = createAction('inviteFlow/saveBackendErrors')
const previousStep = createAction('inviteFlow/previousStep')

export const selectUserGroup = createAction('inviteFlow/userGroups/select')

const ERROR_TYPES = {
  validation: 'bulk_validation',
  quantity: 'quantity_overflow',
  trial_quantity: 'trial_quantity_overflow',
  cant_assign_user_group: 'cant_assign_user_group'
}

const setFormBulkErrors = (dispatch, response) => {
  const { payload, type } = response
  switch (type) {
    case ERROR_TYPES.validation: {
      const error = new FormError()
      error.payload = translateBackendErrorsToForm(payload)
      throw error
    }
    case ERROR_TYPES.cant_assign_user_group:
    case ERROR_TYPES.quantity:
    case ERROR_TYPES.trial_quantity: {
      return false
    }
    default:
      return dispatch(showHttpError(response))
  }
}

const validate = createAsyncThunk(
  'inviteFlow/validate',
  async ({ data, stepId }, { rejectWithValue, dispatch }) => {
    dispatch(dismissAlert())

    const [success, response] = await validateInvitations(data)

    if (!success) {
      switch (response.type) {
        case ERROR_TYPES.validation: {
          // On validation error render list of errors in field notification
          if (stepId >= 0) { dispatch(moveToStep(stepId)) }
          return rejectWithValue(response)
        }
        case ERROR_TYPES.cant_assign_user_group:
        case ERROR_TYPES.quantity:
        case ERROR_TYPES.trial_quantity: {
          if (stepId >= 0) { dispatch(moveToStep(stepId)) }
          // On quantity overflow render update seats popup
          const {
            invitations_quantity: invitationsQuantity,
            min_quantity: minQuantity
          } = response.payload

          const featureKey = response.type === ERROR_TYPES.cant_assign_user_group
            ? 'user_groups'
            : 'organization_invitation'

          dispatch(moveToChangeSubscription({
            featureKey,
            minQuantity,
            invitationsQuantity,
            onSuccess: () => dispatch(validate({ data, stepId }))
          }))

          return rejectWithValue(response)
        }
        default: {
          dispatch(showHttpError(response))
          return rejectWithValue(response)
        }
      }
    }

    return data
  }
)

export const validateAndGoNext = (data) => async (dispatch) => {
  if (!data.invitations.length) {
    return dispatch(showError(I18n.t('publisher.organization.invite_flow.add_emails_step.empty_list')))
  }

  const { payload, error } = await dispatch(validate({ data, nextStep: true }))

  if (error) {
    await dispatch(saveBackendErrors(payload))
    setFormBulkErrors(dispatch, payload)
    return false
  }

  changeSet.dirty('new-invitations-wizard')
  dispatch(nextStep())
  return true
}

export const validateAndGoBack = (data) => async (dispatch) => {
  if (!data.invitations.length) {
    return dispatch(showError(I18n.t('publisher.organization.invite_flow.add_emails_step.empty_list')))
  }

  if (!data.invitations.length) {
    await dispatch(resetFormInvitations())
    dispatch(previousStep())
    return false
  }

  const { payload, error } = await dispatch(validate({ data }))

  if (error) {
    await dispatch(saveBackendErrors(payload))
    setFormBulkErrors(dispatch, payload)
    return false
  }

  dispatch(previousStep())
  return true
}

export const fetchUserGroups = createAsyncThunk(
  'inviteFlow/userGroups/fetch',
  async (query, { dispatch, rejectWithValue }) => {
    const [success, response] = await listGroups({ filter_term: query, page_size: 20 })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return response
  }
)

const createUserGroup = createAsyncThunk(
  'inviteFlow/userGroups/create',
  async (params, { _, rejectWithValue }) => {
    const [success, response] = await createGroup(params)

    if (!success) {
      return rejectWithValue(response)
    }

    return response
  }
)

export const createAndSelectUserGroup = (params) => async (dispatch) => {
  const { payload, error } = await dispatch(createUserGroup(params))
  if (error) {
    dispatch(setFormErrors(payload))
  } else {
    dispatch(selectUserGroup(payload.id))
  }
}

export const fetchManagedEventTypes = createAsyncThunk(
  'inviteFlow/managedEventTypes/fetch',
  async (_, { rejectWithValue, dispatch, getState }) => {
    const state = getState()
    const sortOrder = managedEventTypesSortOrderSelector(state)
    const { currentPage, pageSize } = managedEventTypesPaginationSelector(state)
    const selectedGroupId = selectedGroupIdSelector(state)
    const [success, response] = await getManagedEventTypes(
      {
        sortOrder,
        page: currentPage,
        pageSize,
        groupId: selectedGroupId,
        onlyOrgOwned: !selectedGroupId
      }
    )

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(null)
    }

    return response
  }
)

const initialState = {
  currentStepIndex: 0,
  steps: [],
  isNextStepLoading: false,
  isPreviousStepLoading: false,
  errors: {},
  form: {
    eventTypeIds: [],
    invitations: [],
    groupId: null
  },
  userGroups: {
    list: [],
    isLoading: false,
    selected: null,
    anyGroups: null
  },
  managedEventTypes: {
    list: [],
    isLoading: false,
    selectedIds: [],
    sortOrder: 'asc',
    pagination: {
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
    },
  },
  modal: {
    success: false,
    open: false
  },
  mfa: {
    token: null,
    open: false
  },
  recaptcha: {
    callback: Promise.resolve(null),
    token: null
  },
  role: 'user'
}

const slice = createSlice({
  name: 'inviteFlow',
  initialState,
  reducers: {
    resetState: () => initialState,
    setSteps(state, { payload }) {
      state.steps = payload
    },
    toggleSort(state) {
      state.managedEventTypes.sortOrder = state.managedEventTypes.sortOrder === 'asc' ? 'desc' : 'asc'
    },
    setPage(state, { payload: page }) {
      state.managedEventTypes.pagination.currentPage = page
    },
    setAnyGroups(state, { payload: anyGroups }) {
      state.userGroups.anyGroups = anyGroups
    },
    setRecaptchaCallback(state, { payload }) {
      state.recaptcha.callback = payload
    },
    removeBackendError(state, { payload }) {
      state.errors = transform(state.errors, (result, value, key) => {
        if (payload !== key) {
          result[key] = value
        }

        return result
      }, {})
    },
    setRole(state, { payload }) {
      state.role = payload
    },
    setModalSuccessState(state, { payload }) {
      state.modal.success = payload
    },
    openAddUserModal(state) {
      state.modal.open = true
    },
    closeAddUserModal(state) {
      state.modal.open = false
    },
    openMfa(state) {
      state.mfa.open = true
    },
    closeMfa(state) {
      state.mfa.open = false
    }
  },
  extraReducers: (builder) => {
    builder.addCase('inviteFlow/create/pending', (state) => {
      state.isNextStepLoading = true
    })
    builder.addCase('inviteFlow/create/fulfilled', (state) => {
      state.isNextStepLoading = false
    })
    builder.addCase('inviteFlow/create/rejected', (state) => {
      state.isNextStepLoading = false
    })
    builder.addCase(validate.pending, (state, { meta }) => {
      if (meta.arg?.nextStep) {
        state.isNextStepLoading = true
      } else {
        state.isPreviousStepLoading = true
      }
    })
    builder.addCase(validate.fulfilled, (state, { meta, payload }) => {
      state.form = payload
      if (meta.arg?.nextStep) {
        state.isNextStepLoading = false
      } else {
        state.isPreviousStepLoading = false
      }
    })
    builder.addCase(validate.rejected, (state, { meta }) => {
      if (meta.arg?.nextStep) {
        state.isNextStepLoading = false
      } else {
        state.isPreviousStepLoading = false
      }
    })
    builder.addCase(createUserGroup.pending, (state) => {
      state.userGroups.isLoading = true
    })
    builder.addCase(createUserGroup.fulfilled, (state, { payload }) => {
      state.userGroups.isLoading = false

      state.userGroups.list = [
        ...state.userGroups.list,
        payload
      ]
    })
    builder.addCase(createUserGroup.rejected, (state) => {
      state.userGroups.isLoading = false
    })
    builder.addCase(selectUserGroup, (state, { payload }) => {
      state.managedEventTypes = initialState.managedEventTypes
      state.userGroups.selected = payload
        ? state.userGroups.list.find((group) => group.id === payload)
        : null
    })
    builder.addCase(fetchUserGroups.pending, (state) => {
      state.userGroups.isLoading = true
    })
    builder.addCase(fetchUserGroups.fulfilled, (state, { payload }) => {
      if (payload.collection.length) {
        state.userGroups.list = payload.collection
      } else {
        state.userGroups.list = []
      }

      state.userGroups.isLoading = false
    })
    builder.addCase(fetchUserGroups.rejected, (state) => {
      state.userGroups.isLoading = false
    })
    builder.addCase(nextStep, (state) => {
      if (state.currentStepIndex + 1 < state.steps.length) {
        state.currentStepIndex += 1
      }
    })
    builder.addCase(previousStep, (state) => {
      if (state.currentStepIndex > 0) {
        state.currentStepIndex -= 1
      }
    })
    builder.addCase(moveToStep, (state, { payload }) => {
      state.currentStepIndex = payload
    })
    builder.addCase(fetchManagedEventTypes.pending, (state) => {
      state.managedEventTypes.isLoading = true
    })
    builder.addCase(fetchManagedEventTypes.fulfilled, (state, action) => {
      state.managedEventTypes.isLoading = false
      state.managedEventTypes.list = action.payload.collection
      state.managedEventTypes.pagination.currentPage = action.payload.pagination.current_page
      state.managedEventTypes.pagination.pageSize = action.payload.pagination.page_size
      state.managedEventTypes.pagination.totalCount = action.payload.pagination.total_count
    })
    builder.addCase(fetchManagedEventTypes.rejected, (state) => {
      state.managedEventTypes.isLoading = false
    })
    builder.addCase(resetFormInvitations, (state) => {
      state.form.invitations = []
    })
    builder.addCase(saveBackendErrors, (state, { payload: { type, payload } }) => {
      if (type !== ERROR_TYPES.validation) return

      state.errors = translateBackendErrorsToForm(payload)
    })
  }
})

const create = createAsyncThunk(
  'inviteFlow/create',
  async (data, { rejectWithValue, dispatch, getState }) => {
    const [success, response] = await createInvitations(data)

    if (!success) {
      if (response.type === 'race_condition') {
        dispatch(showError(I18n.t('publisher.billing.failure.please_try_again')))
      } else if (response.type === 'auth' && response.payload.type === 'mfa_required') {
        dispatch(slice.actions.openMfa())
      } else {
        dispatch(showHttpError(response))
      }
      return rejectWithValue(response)
    }

    const isSingleOrganization = isSingleSelector(getState())
    if (isSingleOrganization) {
      dispatch(rootApi.util.invalidateTags(['CURRENT_ORGANIZATION']))
    }

    dispatch(fetchStatistics())
    return response
  }
)

export const validateAndCreate = (data) => async (dispatch, getState) => {
  const { payload, error } = await dispatch(validate({ data, stepId: 1 }))

  if (error) {
    await dispatch(saveBackendErrors(payload))
    setFormBulkErrors(dispatch, payload)
    return false
  }

  const callback = recaptchaCallbackSelector(getState())
  const recaptchaToken = await callback()

  const { payload: createPayload, error: createError } = await dispatch(create({ ...data, recaptchaToken }))

  if (createError || (createPayload && createPayload.type === 'race_condition')) { return false }

  changeSet.clear('new-invitations-wizard')
  dispatch(nextStep())
  return true
}

export const initialize = () => async (dispatch, getState) => {
  const isAdminOrGroupAdmin = isAdminOrOwnerOrGroupAdminSelector(getState())

  if (!isAdminOrGroupAdmin) {
    const steps = STEPS.filter((stepName) => stepName !== 'assign_event_types')
    return dispatch(slice.actions.setSteps(steps))
  }

  await Promise.all([
    dispatch(fetchManagedEventTypes()),
    dispatch(fetchUserGroups())
  ])

  const state = getState()

  const managedEventTypes = state.inviteFlow.managedEventTypes.list
  const anyGroups = state.inviteFlow.userGroups.list.length > 0
  const currentUserIsGroupAdminOnly = isOnlyGroupAdminSelector(state)

  const steps = (managedEventTypes.length > 0 || currentUserIsGroupAdminOnly)
    ? STEPS
    : STEPS.filter((stepName) => stepName !== 'assign_event_types')

  dispatch(slice.actions.setAnyGroups(anyGroups))
  return dispatch(slice.actions.setSteps(steps))
}

export const sortManagedEvents = () => async (dispatch) => {
  dispatch(slice.actions.toggleSort())
  dispatch(slice.actions.setPage(1))
  dispatch(fetchManagedEventTypes())
}

export const fetchPage = (page) => (dispatch) => {
  dispatch(slice.actions.setPage(page))
  dispatch(fetchManagedEventTypes())
}

export const { reducer, actions: {
  removeBackendError,
  setRecaptchaCallback,
  setSteps,
  setRole,
  setModalSuccessState,
  openAddUserModal,
  closeAddUserModal,
  closeMfa,
} } = slice

export const validateAndCreateFromModal = (data) => async (dispatch, getState) => {
  const { payload, error } = await dispatch(validate({ data, stepId: 1 }))

  if (error) {
    await dispatch(saveBackendErrors(payload))
    setFormBulkErrors(dispatch, payload)
    return false
  }

  const callback = recaptchaCallbackSelector(getState())
  const recaptchaToken = await callback()

  const { payload: createPayload, error: createError } = await dispatch(create({ ...data, recaptchaToken, modal: true }))

  if (createError || (createPayload && createPayload.type === 'race_condition')) { return false }

  if (data.invitations.length === 1) {
    dispatch(showSuccess(I18n.t('publisher.add_users_modal.invite_sent')))
  } else {
    dispatch(showSuccess(I18n.t('publisher.add_users_modal.invites_sent', { count: data.invitations.length })))
  }

  await dispatch(resetFormInvitations())
  dispatch(slice.actions.closeMfa())
  dispatch(setModalSuccessState(true))

  return true
}

export const resetPageState = () => async (dispatch) => {
  dispatch(dismissAlert())
}
